import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import AppleSignInButton from './AppleSignInButton';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";
const TERMS_VERSION = "2026-06-01";

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast({ title: 'Please accept the Terms', description: 'You must agree to the Terms of Service and Privacy Policy to create an account.', variant: 'destructive' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Password Mismatch', description: 'Passwords do not match. Please try again.', variant: 'destructive' });
      return;
    }
    if (formData.password.length < 8) {
      toast({ title: 'Password Too Short', description: 'Password must be at least 8 characters long.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await signup({ ...formData, accepted_terms: true, terms_version: TERMS_VERSION });
      toast({ title: 'Account Created!', description: 'Welcome to RideBuraq. Your first-class ride is just a tap away.' });
      navigate('/dashboard');
    } catch (error) {
      toast({ title: 'Signup Failed', description: error.message || 'Something went wrong. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl";
  const labelCls = "text-gray-400 text-xs tracking-wide uppercase";

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5 py-16">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full bg-orange-500/10 blur-[120px]" />

      <button
        data-testid="back-btn"
        onClick={() => navigate('/')}
        className="absolute left-5 top-6 w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
        aria-label="Back"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="RideBuraq" className="w-14 h-14 object-contain mb-5" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Join RideBuraq</p>
          <h1 className="text-4xl font-semibold tracking-tight">Create Account</h1>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className={labelCls}>First Name</Label>
                <Input id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleChange} className={inputCls} placeholder="John" />
              </div>
              <div>
                <Label htmlFor="lastName" className={labelCls}>Last Name</Label>
                <Input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleChange} className={inputCls} placeholder="Doe" />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className={labelCls}>Email</Label>
              <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className={labelCls}>Password</Label>
              <Input id="password" name="password" type="password" required value={formData.password} onChange={handleChange} className={inputCls} placeholder="At least 8 characters" />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className={labelCls}>Confirm Password</Label>
              <Input id="confirmPassword" name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className={inputCls} placeholder="Confirm your password" />
            </div>
            <label htmlFor="agree-terms" className="flex items-start gap-3 cursor-pointer pt-1">
              <input
                id="agree-terms"
                type="checkbox"
                data-testid="agree-terms-checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/[0.03] accent-orange-500 cursor-pointer"
              />
              <span className="text-[12px] text-gray-400 leading-relaxed">
                I acknowledge that RideBuraq is a technology platform that connects riders with independent
                contractor drivers (it is not a transportation carrier), and I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-orange-400 hover:text-orange-300 underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-orange-400 hover:text-orange-300 underline">Privacy Policy</Link>.
              </span>
            </label>
            <button
              type="submit"
              data-testid="signup-submit-btn"
              disabled={loading || !agreed}
              className="w-full rounded-full py-3.5 mt-2 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-900/30 hover:shadow-orange-500/30 active:scale-[0.99] transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center"
            >
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating Account...</>) : 'Create Account'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] tracking-wide uppercase text-gray-600">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <AppleSignInButton requireConsent consentGiven={agreed} termsVersion={TERMS_VERSION} />
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
